import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError, homeApi, type SilkHomeSite } from '@/api'

type HomeSiteContextValue = {
  site: SilkHomeSite | null
  loading: boolean
  error: string | null
  refresh: (opts?: { silent?: boolean }) => Promise<void>
}

const HomeSiteContext = createContext<HomeSiteContextValue | null>(null)

export function HomeSiteProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const [site, setSite] = useState<SilkHomeSite | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const inflightRef = useRef<Promise<void> | null>(null)
  const siteRef = useRef<SilkHomeSite | null>(null)

  useEffect(() => {
    siteRef.current = site
  }, [site])

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? Boolean(siteRef.current)
    while (inflightRef.current) {
      await inflightRef.current
    }
    if (!silent) {
      setLoading(true)
    }
    setError(null)

    const run = (async () => {
      try {
        const res = await homeApi.site()
        setSite(res)
        setError(null)
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setError(msg)
        if (!silent) {
          setSite(null)
        }
      } finally {
        if (!silent) {
          setLoading(false)
        }
        inflightRef.current = null
      }
    })()

    inflightRef.current = run
    await run
  }, [])

  useEffect(() => {
    void import('@/features/home/pages/HomePage')
  }, [])

  useEffect(() => {
    void refresh({ silent: Boolean(siteRef.current) })
  }, [i18n.language, refresh])

  const value = useMemo(
    () => ({ site, loading, error, refresh }),
    [site, loading, error, refresh],
  )

  return <HomeSiteContext.Provider value={value}>{children}</HomeSiteContext.Provider>
}

export function useOptionalHomeSiteContext(): HomeSiteContextValue | null {
  return useContext(HomeSiteContext)
}

export function useHomeSiteContext(): HomeSiteContextValue {
  const ctx = useOptionalHomeSiteContext()
  if (!ctx) {
    throw new Error('useHomeSiteContext must be used within HomeSiteProvider')
  }
  return ctx
}
